import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import api from "@/lib/api";

/**
 * GuestContext
 *
 * When a guest lands with `?g=<slug>` (from a personalised invite link),
 * we look up their existing RSVP so we can:
 *   - greet them by name on the hero
 *   - show their table / meal choice
 *   - prefill the RSVP form when they open it, and switch it into "edit" mode
 *
 * The slug is persisted per-device once seen so returning visitors keep
 * their personalised experience even if the URL param is dropped.
 */

const SLUG_KEY = "em_guest_slug";
const NAME_KEY = "em_guest_name";  // already used elsewhere for location welcomes

const GuestContext = createContext({
  guestSlug: null,
  guestRsvp: null,
  loading: false,
  refresh: () => {},
  clearGuest: () => {},
  lookupByName: async (_name) => null,
});

export const useGuest = () => useContext(GuestContext);

export const GuestProvider = ({ children }) => {
  const { isAuthed, authReady } = useAuth();
  const [params, setParams] = useSearchParams();
  const location = useLocation();
  const [guestSlug, setGuestSlug] = useState(() => {
    try { return localStorage.getItem(SLUG_KEY) || null; } catch { return null; }
  });
  const [guestRsvp, setGuestRsvp] = useState(null);
  const [loading, setLoading] = useState(false);

  // 1) Capture ?g=<slug> from the URL and persist it. Strip it after so the URL stays clean.
  useEffect(() => {
    const slugFromUrl = params.get("g");
    if (slugFromUrl) {
      const norm = slugFromUrl.trim().toLowerCase();
      try { localStorage.setItem(SLUG_KEY, norm); } catch { /* ignore */ }
      setGuestSlug(norm);
      // Remove ?g=… from URL so future refreshes don't loop
      const next = new URLSearchParams(params);
      next.delete("g");
      setParams(next, { replace: true });
    }
  }, [params, setParams]);

  const fetchRsvp = useCallback(async () => {
    if (!isAuthed || !guestSlug) { setGuestRsvp(null); return; }
    setLoading(true);
    try {
      const { data } = await api.get(`/rsvp/lookup?slug=${encodeURIComponent(guestSlug)}`);
      setGuestRsvp(data || null);
      if (data && data.name) {
        try {
          const first = String(data.name).trim().split(/\s+/)[0];
          if (first) localStorage.setItem(NAME_KEY, first);
        } catch { /* ignore */ }
      }
    } catch {
      setGuestRsvp(null);
    } finally {
      setLoading(false);
    }
  }, [isAuthed, guestSlug]);

  useEffect(() => {
    if (!authReady) return;
    fetchRsvp();
  }, [authReady, fetchRsvp, location.pathname]);

  const lookupByName = useCallback(async (name) => {
    if (!name) return null;
    try {
      const { data } = await api.get(`/rsvp/lookup?name=${encodeURIComponent(name)}`);
      return data || null;
    } catch { return null; }
  }, []);

  const clearGuest = useCallback(() => {
    setGuestSlug(null);
    setGuestRsvp(null);
    try {
      localStorage.removeItem(SLUG_KEY);
      localStorage.removeItem(NAME_KEY);
    } catch { /* ignore */ }
  }, []);

  const value = useMemo(() => ({
    guestSlug,
    guestRsvp,
    loading,
    refresh: fetchRsvp,
    clearGuest,
    lookupByName,
  }), [guestSlug, guestRsvp, loading, fetchRsvp, clearGuest, lookupByName]);

  return <GuestContext.Provider value={value}>{children}</GuestContext.Provider>;
};

export default GuestContext;
